// /opt/isp_erp/frontend/src/pages/admin/clientes/components/PagosCliente.jsx
import React, { useState, useEffect } from 'react';
import axios from '../../../../api/axios';
import { format } from 'date-fns';
import { pagosApi } from '../../../../api/finanzas/pagos.api';
import './PagosCliente.css';

export default function PagosCliente({ customerId }) {
  const [pagos, setPagos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [resumen, setResumen] = useState(null);
  const [procesando, setProcesando] = useState(false);

  // Estado para nuevo pago
  const [nuevoPago, setNuevoPago] = useState({
    monto: '',
    metodo: 'EFECTIVO',
    referencia: '',
    notas: ''
  });

  // Mapeo de métodos de pago con iconos y nombres
  const metodosPago = [
    { value: 'EFECTIVO', label: 'Efectivo', icon: '💵' },
    { value: 'TRANSFERENCIA', label: 'Transferencia', icon: '🏦' },
    { value: 'TARJETA_CREDITO', label: 'Tarjeta Crédito', icon: '💳' },
    { value: 'TARJETA_DEBITO', label: 'Tarjeta Débito', icon: '💳' },
    { value: 'DEPOSITO', label: 'Depósito', icon: '🏧' },
    { value: 'CHEQUE', label: 'Cheque', icon: '📝' },
    { value: 'OTRO', label: 'Otro', icon: '🔄' }
  ];

  useEffect(() => {
    fetchPagos();
    fetchResumen();
  }, [customerId]);

  const fetchPagos = async () => {
    try {
      const response = await pagosApi.listarPorCliente(customerId, { limit: 50 });
      setPagos(response.data);
    } catch (error) {
      console.error('Error cargando pagos:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchResumen = async () => {
    try {
      const response = await pagosApi.resumenCliente(customerId);
      setResumen(response.data);
    } catch (error) {
      console.error('Error cargando resumen:', error);
    }
  };

  const handleRegistrarPago = async () => {
    // Validaciones
    if (!nuevoPago.monto || nuevoPago.monto <= 0) {
      alert('Ingrese un monto válido');
      return;
    }

    setProcesando(true);
    try {
      await pagosApi.crear({
        customer_id: parseInt(customerId),
        monto: parseFloat(nuevoPago.monto),
        metodo: nuevoPago.metodo,
        referencia: nuevoPago.referencia || null,
        notas: nuevoPago.notas || null
      });

      setShowModal(false);
      setNuevoPago({ monto: '', metodo: 'EFECTIVO', referencia: '', notas: '' });
      await fetchPagos();
      await fetchResumen();
      alert('✅ Pago registrado correctamente');
    } catch (error) {
      console.error('Error registrando pago:', error);
      alert(error.response?.data?.detail || 'Error al registrar pago');
    } finally {
      setProcesando(false);
    }
  };

  const handleAnularPago = async (pagoId) => {
    if (!window.confirm('¿Está seguro de anular este pago?')) return;

    const motivo = prompt('Motivo de anulación:');
    if (!motivo) return;

    setProcesando(true);
    try {
      await pagosApi.anular(pagoId, motivo);
      await fetchPagos();
      await fetchResumen();
      alert('✅ Pago anulado correctamente');
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al anular pago');
    } finally {
      setProcesando(false);
    }
  };

  const getMetodoInfo = (metodo) => {
    const encontrado = metodosPago.find(m => m.value === metodo);
    return encontrado || { icon: '💰', label: metodo };
  };

  const formatearFecha = (fecha) => {
    try {
      return format(new Date(fecha), 'dd/MM/yyyy HH:mm');
    } catch {
      return 'Fecha inválida';
    }
  };

  if (loading) {
    return (
      <div className="pagos-loading-container">
        <div className="pagos-loading-spinner"></div>
        <p>Cargando historial de pagos...</p>
      </div>
    );
  }

  return (
    <div className="pagos-cliente">
      {/* Resumen financiero del cliente */}
      {resumen && (
        <div className="pagos-resumen">
          <div className="resumen-card resumen-pagado">
            <div className="resumen-icon">💰</div>
            <div>
              <p className="resumen-label">Total Pagado</p>
              <p className="resumen-value">${resumen.total_pagado?.toFixed(2)}</p>
            </div>
          </div>
          <div className="resumen-card resumen-facturado">
            <div className="resumen-icon">📊</div>
            <div>
              <p className="resumen-label">Total Facturado</p>
              <p className="resumen-value">${resumen.total_facturado?.toFixed(2)}</p>
            </div>
          </div>
          <div className="resumen-card resumen-saldo">
            <div className="resumen-icon">⏳</div>
            <div>
              <p className="resumen-label">Saldo Pendiente</p>
              <p className="resumen-value">${resumen.saldo_pendiente?.toFixed(2)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Header con título y botón */}
      <div className="pagos-header">
        <div className="pagos-header-left">
          <div className="pagos-header-icon">💰</div>
          <div>
            <h2>Historial de Pagos</h2>
            <p>Registro de pagos realizados por el cliente</p>
          </div>
        </div>
        <button
          onClick={() => setShowModal(true)}
          disabled={procesando}
          className="pagos-btn-primary"
        >
          <span>💰</span>
          Registrar Nuevo Pago
        </button>
      </div>

      {/* Tabla de pagos */}
      {pagos.length === 0 ? (
        <div className="pagos-empty">
          <div className="empty-icon">💰</div>
          <h3>No hay pagos registrados</h3>
          <p>Utilice el botón "Registrar Nuevo Pago" para agregar el primer pago</p>
        </div>
      ) : (
        <div className="pagos-table-container">
          <table className="pagos-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Monto</th>
                <th>Método</th>
                <th>Referencia</th>
                <th>Estado</th>
                <th>Cobrado por</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {pagos.map((pago) => {
                const metodoInfo = getMetodoInfo(pago.metodo);
                return (
                  <tr key={pago.id} className="pago-row">
                    <td className="pago-fecha">
                      <span className="fecha-valor">{formatearFecha(pago.fecha_pago)}</span>
                    </td>
                    <td className="pago-monto">
                      <span className="monto-valor">${pago.monto?.toFixed(2)}</span>
                    </td>
                    <td>
                      <span className="pago-metodo">
                        <span className="metodo-icon">{metodoInfo.icon}</span>
                        <span className="metodo-label">{metodoInfo.label}</span>
                      </span>
                    </td>
                    <td className="pago-referencia">
                      {pago.referencia || <span className="text-muted">Sin referencia</span>}
                    </td>
                    <td>
                      <span className={`pago-estado estado-${pago.estado?.toLowerCase()}`}>
                        {pago.estado === 'COMPLETADO' && '✅ '}
                        {pago.estado === 'ANULADO' && '❌ '}
                        {pago.estado === 'PENDIENTE' && '⏳ '}
                        {pago.estado}
                      </span>
                    </td>
                    <td className="pago-cobrador">
                      {pago.registrado_por?.username || <span className="text-muted">Sistema</span>}
                    </td>
                    <td className="pago-acciones">
                      {pago.estado === 'COMPLETADO' && (
                        <button
                          onClick={() => handleAnularPago(pago.id)}
                          disabled={procesando}
                          className="pago-btn-anular"
                          title="Anular pago"
                        >
                          ❌ Anular
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal para nuevo pago */}
      {showModal && (
        <div className="pagos-modal-overlay">
          <div className="pagos-modal-content">
            <div className="pagos-modal-header">
              <div className="modal-header-icon">💰</div>
              <div>
                <h3>Registrar Nuevo Pago</h3>
                <p>Complete los detalles del pago</p>
              </div>
            </div>

            <div className="pagos-modal-body">
              {/* Monto */}
              <div className="form-group">
                <label>
                  Monto a pagar <span className="required">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={nuevoPago.monto}
                  onChange={(e) => setNuevoPago({...nuevoPago, monto: e.target.value})}
                  className="form-input"
                  placeholder="0.00"
                  autoFocus
                  disabled={procesando}
                />
              </div>

              {/* Método de pago */}
              <div className="form-group">
                <label>Método de pago</label>
                <select
                  value={nuevoPago.metodo}
                  onChange={(e) => setNuevoPago({...nuevoPago, metodo: e.target.value})}
                  className="form-select"
                  disabled={procesando}
                >
                  {metodosPago.map(metodo => (
                    <option key={metodo.value} value={metodo.value}>
                      {metodo.icon} {metodo.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Referencia */}
              <div className="form-group">
                <label>
                  Referencia <span className="optional">(opcional)</span>
                </label>
                <input
                  type="text"
                  value={nuevoPago.referencia}
                  onChange={(e) => setNuevoPago({...nuevoPago, referencia: e.target.value})}
                  className="form-input"
                  placeholder="N° de transferencia, cheque, etc."
                  disabled={procesando}
                />
              </div>

              {/* Notas */}
              <div className="form-group">
                <label>
                  Notas <span className="optional">(opcional)</span>
                </label>
                <textarea
                  value={nuevoPago.notas}
                  onChange={(e) => setNuevoPago({...nuevoPago, notas: e.target.value})}
                  className="form-textarea"
                  rows="3"
                  placeholder="Observaciones adicionales..."
                  disabled={procesando}
                />
              </div>
            </div>

            {/* Botones del modal */}
            <div className="pagos-modal-footer">
              <button
                onClick={() => {
                  setShowModal(false);
                  setNuevoPago({ monto: '', metodo: 'EFECTIVO', referencia: '', notas: '' });
                }}
                className="pagos-btn-secondary"
                disabled={procesando}
              >
                Cancelar
              </button>
              <button
                onClick={handleRegistrarPago}
                disabled={procesando || !nuevoPago.monto}
                className="pagos-btn-primary"
              >
                {procesando ? (
                  <>
                    <span className="loading-spinner-small"></span>
                    Procesando...
                  </>
                ) : (
                  <>
                    <span>💰</span>
                    Registrar Pago
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
